import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { Person, PersonClient } from '../../utils/apiClient';
import AppContext from '../../AppContext';
import { ReactSelectDefault } from '../../common/components/ReactSelectDefault/ReactSelect';
import { parseApiError } from '../../utils/apiError';
import {
  MergeCandidatePair,
  MergePerson,
  MergeResult,
  formatBirthDate,
  getDuplicates,
  personFullName,
} from '../../utils/personMergeApi';
import MergeModal from './MergeModal';
import './PersonDuplicates.scss';

interface MergeTarget {
  primaryId: number;
  secondaryId: number;
}

const PersonSummary: React.FC<{ person: MergePerson; suggested: boolean }> = ({ person, suggested }) => (
  <div className="dup-person">
    <div className="dup-person__name">
      {personFullName(person)}
      {suggested && <span className="dup-person__badge">рекомендуем оставить</span>}
    </div>
    <div className="dup-person__meta">
      {formatBirthDate(person.birthDate)} · {person.city || 'город не указан'}
    </div>
    <div className="dup-person__meta">
      {person.hasAccount ? `Вход: ${person.email}` : 'Анкета без входа в систему'}
      {person.isChild ? ' · ребёнок' : ''}
    </div>
    <div className="dup-person__meta">
      Заявок: {person.applications.length}
      {person.applications.length > 0 && ` (${person.applications.map((a) => a.tournamentName).join(', ')})`}
    </div>
    {person.roles.length > 0 && <div className="dup-person__meta">Роли: {person.roles.join(', ')}</div>}
  </div>
);

const PersonDuplicates: React.FC = () => {
  const appContext = useContext(AppContext);
  const [pairs, setPairs] = useState<MergeCandidatePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPossible, setShowPossible] = useState(true);
  const [target, setTarget] = useState<MergeTarget | null>(null);

  // Ручной выбор двух анкет (если ФИО записано по-разному и автоматически дубликат не нашёлся)
  const [people, setPeople] = useState<Person[]>([]);
  const [manualA, setManualA] = useState<number | undefined>(undefined);
  const [manualB, setManualB] = useState<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      setPairs(await getDuplicates());
      setError('');
    } catch (ex) {
      setError(parseApiError(ex).message || 'Не удалось загрузить список дубликатов.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    new PersonClient(ApiUrl, axios).getPersons().then(setPeople).catch(() => undefined);
  }, [load]);

  const personOptions = useMemo(
    () => people
      .filter((p) => p.id != null)
      .map((p) => ({
        value: p.id as number,
        label: `${p.lastName} ${p.firstName}${p.patronymic ? ` ${p.patronymic}` : ''} · ${new Date(p.birthDate).toLocaleDateString('ru-RU')}${p.email ? ` · ${p.email}` : ''} · #${p.id}`,
      })),
    [people]
  );

  const visiblePairs = pairs.filter((p) => showPossible || p.confidence === 'exact');
  const exactCount = pairs.filter((p) => p.confidence === 'exact').length;

  const openPair = (pair: MergeCandidatePair) => {
    const secondary = pair.suggestedPrimaryId === pair.a.id ? pair.b : pair.a;
    setTarget({ primaryId: pair.suggestedPrimaryId, secondaryId: secondary.id });
  };

  const openManual = () => {
    if (!manualA || !manualB || manualA === manualB) {
      appContext?.setDisappearingNotification({ message: 'Выберите две разные анкеты.' });
      return;
    }
    setTarget({ primaryId: manualA, secondaryId: manualB });
  };

  const handleMerged = async (result: MergeResult) => {
    setTarget(null);
    setManualA(undefined);
    setManualB(undefined);
    appContext?.setDisappearingNotification({ message: result.message });
    await load();
    new PersonClient(ApiUrl, axios).getPersons().then(setPeople).catch(() => undefined);
  };

  return (
    <div className="person-duplicates">
      <header className="person-duplicates__header">
        <div>
          <h1 className="person-duplicates__title">Дубликаты анкет</h1>
          <p className="person-duplicates__lead">
            Если участника завёл администратор, а потом он сам зарегистрировался на сайте, получаются две анкеты одного человека.
            Здесь они находятся по ФИО и дате рождения и объединяются в одну: заявки, роли и тренер переносятся.
          </p>
        </div>
        <Link to="/admin" className="btn btn--back">К панели администратора</Link>
      </header>

      <section className="person-duplicates__manual">
        <h2>Объединить вручную</h2>
        <p>Если ФИО записано по-разному и автоматически анкеты не нашлись — выберите их сами.</p>
        <div className="person-duplicates__manual-row">
          <ReactSelectDefault<number>
            options={personOptions}
            value={manualA}
            onChange={(o) => setManualA(o.value ? Number(o.value) : undefined)}
            placeholder="Первая анкета"
            params={{ width: 340, height: 40 }}
            filterByLabel
          />
          <ReactSelectDefault<number>
            options={personOptions}
            value={manualB}
            onChange={(o) => setManualB(o.value ? Number(o.value) : undefined)}
            placeholder="Вторая анкета"
            params={{ width: 340, height: 40 }}
            filterByLabel
          />
          <button type="button" className="btn" onClick={openManual}>Сравнить и объединить</button>
        </div>
      </section>

      <div className="person-duplicates__toolbar">
        <h2>
          Найдено совпадений: {exactCount}
          {pairs.length > exactCount && <span className="person-duplicates__muted"> + возможных: {pairs.length - exactCount}</span>}
        </h2>
        <label className="person-duplicates__toggle">
          <input type="checkbox" checked={showPossible} onChange={(e) => setShowPossible(e.target.checked)} />
          Показывать возможные (ФИО и год рождения)
        </label>
      </div>

      {loading && <div className="person-duplicates__state">Загрузка…</div>}
      {!loading && error && <div className="person-duplicates__alert" role="alert">{error}</div>}
      {!loading && !error && visiblePairs.length === 0 && (
        <div className="person-duplicates__empty">Дубликатов не найдено.</div>
      )}

      <div className="person-duplicates__list">
        {visiblePairs.map((pair) => (
          <div key={`${pair.a.id}-${pair.b.id}`} className="dup-pair">
            <div className="dup-pair__head">
              <span className={`dup-pair__badge dup-pair__badge--${pair.confidence}`}>
                {pair.confidence === 'exact' ? 'ФИО и дата рождения совпали' : 'ФИО и год рождения совпали, дата отличается'}
              </span>
              <button type="button" className="btn" onClick={() => openPair(pair)}>Объединить…</button>
            </div>
            <div className="dup-pair__body">
              <PersonSummary person={pair.a} suggested={pair.suggestedPrimaryId === pair.a.id} />
              <PersonSummary person={pair.b} suggested={pair.suggestedPrimaryId === pair.b.id} />
            </div>
          </div>
        ))}
      </div>

      {target && (
        <MergeModal
          initialPrimaryId={target.primaryId}
          initialSecondaryId={target.secondaryId}
          onClose={() => setTarget(null)}
          onMerged={handleMerged}
        />
      )}
    </div>
  );
};

export default PersonDuplicates;
